import { useParams, Link, Navigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { AMNAYA_PEETHAS, peethaWithOverrides } from "@/lib/amnayaPeethas";
import Seo from "@/components/Seo";
import { ArrowLeft, BookOpen, Quote, User, MapPin, Droplet, Users, Globe, Landmark } from "lucide-react";

const Fact = ({ icon: Icon, label, children }) => (
  <div className="flex gap-3 border-b border-line py-3 last:border-0">
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-saffron" />
    <div className="min-w-0">
      <p className="text-xs uppercase tracking-wider text-ink-3">{label}</p>
      <p className="mt-0.5 text-[15px] text-ink">{children}</p>
    </div>
  </div>
);

export default function AmnayaPeethaDetail() {
  const { slug } = useParams();
  const { pick } = useLang();
  const settings = useSettings();
  const base = AMNAYA_PEETHAS.find((p) => p.slug === slug);
  if (!base) return <Navigate to="/amnaya-peethas" replace />;
  const p = peethaWithOverrides(base, settings);

  return (
    <article data-testid={`peetha-detail-${p.slug}`} className="bg-[var(--bg)]">
      <Seo path={`/amnaya-peethas/${p.slug}`} title={`${p.name} — Amnaya Peetha — Dharmartha`} description={p.summary} />

      <div className="relative h-56 overflow-hidden md:h-80">
        <img src={p.image} alt={p.name} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 mx-auto max-w-4xl px-5 pb-6 md:px-8">
          <p className="overline-label text-gold">{p.direction} · {pick("", p.direction_en + " Āmnāya")}</p>
          <h1 className="mt-1 font-deva text-3xl text-white deva-lh md:text-4xl">{p.deva_name}</h1>
          <p className="font-serif-latin text-lg text-white/90">{p.name}</p>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-5 py-10 md:px-8">
        <Link to="/amnaya-peethas" className="mb-8 inline-flex items-center gap-1.5 text-sm text-ink-2 hover:text-saffron">
          <ArrowLeft className="h-4 w-4" /> {pick("चारों पीठ", "All four Peethas")}
        </Link>

        {/* Mahavakya highlight */}
        <div className="mb-8 rounded-xl border border-[var(--gold)]/40 bg-[var(--surface)] p-6 text-center">
          <Quote className="mx-auto h-5 w-5 text-gold" />
          <p className="mt-2 font-deva text-3xl text-ink deva-lh">{p.mahavakya_deva}</p>
          <p className="mt-1 font-serif-latin text-xl text-saffron">{p.mahavakya}</p>
          <p className="mt-1 text-sm text-ink-3">“{p.mahavakya_meaning}” · {p.veda}</p>
        </div>

        <p className="max-w-[65ch] font-serif-latin text-lg leading-relaxed text-ink-2">{p.summary}</p>

        <div className="mt-8 grid gap-x-10 gap-y-0 md:grid-cols-2">
          <Fact icon={BookOpen} label={pick("वेद", "Veda")}>{p.veda_deva} · {p.veda}</Fact>
          <Fact icon={User} label={pick("प्रथम आचार्य", "First Acharya")}>{p.first_acharya}</Fact>
          <Fact icon={Landmark} label={pick("देवता", "Presiding Deity")}>{p.deity}</Fact>
          <Fact icon={Users} label={pick("सम्प्रदाय", "Sampradaya")}>{p.sampradaya}</Fact>
          <Fact icon={MapPin} label={pick("स्थान", "Location")}>{p.location}</Fact>
          <Fact icon={Droplet} label={pick("पवित्र जल", "Sacred waters")}>{p.river}</Fact>
          <Fact icon={Quote} label={pick("तीर्थ नाम", "Tīrtha names")}>{p.tirtha_names}</Fact>
        </div>

        {/* Current Acharya */}
        <div className="mt-8 rounded-xl border border-line bg-[var(--surface)] p-6" data-testid="peetha-acharya">
          <p className="overline-label text-saffron">{pick("वर्तमान जगद्गुरु", "Current Jagadguru")}</p>
          <p className="mt-2 flex items-center gap-2 font-serif-latin text-lg text-ink"><User className="h-5 w-5 text-saffron" /> {p.current_acharya}</p>
          {p.predecessor && <p className="mt-1 text-sm text-ink-3">{pick("पूर्वज", "Predecessor")}: {p.predecessor}</p>}
          {p.note && <p className="mt-3 text-sm leading-relaxed text-ink-2">{p.note}</p>}
          {p.website && <a href={p.website} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1.5 border border-saffron px-4 py-2 text-sm font-medium text-saffron transition-colors hover:bg-[var(--saffron)] hover:text-white"><Globe className="h-4 w-4" /> {pick("आधिकारिक वेबसाइट", "Official website")}</a>}
        </div>
      </div>
    </article>
  );
}
